/**
 * "Getting started in {org}" — teaches a member the org hub by doing what
 * their level allows (20261011100000). Every level gets the four
 * read/chat/vote steps; Medio and up (canContribute) also share a file, start
 * a poll, post a quest as the org and invite someone. Admins get the Medio
 * steps: the admin-only tools (requests, levels, settings) wait on someone
 * else acting first, so a step for them would sit undone through no fault of
 * the admin. Like the app-wide checklist (gettingStarted/checklist.ts) every
 * step is derived from what the app knows, never ticked by hand.
 *
 * Voting is impossible until someone opens a poll the member can see, so that
 * step is locked and left out of the total until then — a past vote still
 * counts once every poll has closed.
 */
import type { StepStatus } from "@/features/gettingStarted/checklist";
import { canContribute, postAsOrgHref, sortOrgsByName, type MyOrganization, type OrgRole } from "@/lib/organizations";
import type { OrgSection } from "@/lib/orgHub";
import { orgManageRoute } from "../orgRoutes";
import { completedSeenFlag, hiddenFlag, type OrgChecklistFlags } from "./orgChecklistStorage";

export type OrgStepId =
  | "intro" | "board" | "poll" | "members"
  | "file" | "createPoll" | "postQuest" | "invite";

/** Which list a member gets — and so which "seen" / "hidden" flags apply. */
export type OrgChecklistTier = "simple" | "contributor";

/** Where a todo row takes the member: a hub section, or the Assistant to post as the org. */
export type OrgStepTarget = OrgSection | "assistant";

export interface OrgChecklistFacts {
  hasSentMessage: boolean;
  visitedBoard: boolean;
  visitedMembers: boolean;
  hasVoted: boolean;
  /** An open poll the member can see — the polls RPC already hides admins-only ones. */
  hasOpenPoll: boolean;
}

/** Medio and up only. */
export interface OrgContributorFacts {
  hasUploadedFile: boolean;
  hasCreatedPoll: boolean;
  hasPostedQuest: boolean;
  hasInvited: boolean;
}

export interface OrgChecklistStep {
  id: OrgStepId;
  status: StepStatus;
  target: OrgStepTarget;
}

export interface OrgChecklist {
  steps: OrgChecklistStep[];
  done: number;
  total: number;
  complete: boolean;
}

const done = (yes: boolean): StepStatus => (yes ? "done" : "todo");

/** `contributor` null = the Simple list; pass the facts for Medio and up. */
export function buildOrgChecklist(f: OrgChecklistFacts, contributor: OrgContributorFacts | null = null): OrgChecklist {
  const steps: OrgChecklistStep[] = [
    { id: "intro", status: done(f.hasSentMessage), target: "chat" },
    { id: "board", status: done(f.visitedBoard), target: "board" },
    // A Medio's own poll counts as one to vote in: starting one unlocks this.
    { id: "poll", status: f.hasVoted ? "done" : f.hasOpenPoll ? "todo" : "locked", target: "polls" },
    { id: "members", status: done(f.visitedMembers), target: "members" },
  ];
  if (contributor) {
    steps.push(
      { id: "file", status: done(contributor.hasUploadedFile), target: "files" },
      { id: "createPoll", status: done(contributor.hasCreatedPoll), target: "polls" },
      { id: "postQuest", status: done(contributor.hasPostedQuest), target: "assistant" },
      { id: "invite", status: done(contributor.hasInvited), target: "invites" },
    );
  }
  const counted = steps.filter((s) => s.status !== "locked");
  const doneCount = counted.filter((s) => s.status === "done").length;
  return { steps, done: doneCount, total: counted.length, complete: doneCount === counted.length };
}

/** Every member gets a list; Medio and up get the longer one. Null = not a member. */
export function orgChecklistTier(role: OrgRole | null | undefined): OrgChecklistTier | null {
  if (!role) return null;
  return canContribute(role) ? "contributor" : "simple";
}

// ─── Top-bar ring ───────────────────────────────────────────────────────────

/** Where a step goes from anywhere in the app: its hub section, or the Assistant. */
export function orgStepHref(org: Pick<MyOrganization, "id" | "slug">, step: Pick<OrgChecklistStep, "target">): string {
  return step.target === "assistant" ? postAsOrgHref(org.id) : orgManageRoute(org.slug, step.target);
}

/** The first step still to do, in list order — what the daily nudge points at. */
export function nextOrgStep(checklist: OrgChecklist): OrgChecklistStep | null {
  return checklist.steps.find((s) => s.status === "todo") ?? null;
}

/**
 * Orgs whose checklist the ring may show, by name: every org the member is in,
 * minus the ones they hid or already saw finished at their current level.
 */
export function orgChecklistCandidates(
  orgs: readonly MyOrganization[],
  flagsOf: (orgId: string) => OrgChecklistFlags,
): MyOrganization[] {
  return sortOrgsByName(
    orgs.filter((o) => {
      const tier = orgChecklistTier(o.myRole);
      if (!tier) return false;
      const f = flagsOf(o.id);
      return !f[hiddenFlag(tier)] && !f[completedSeenFlag(tier)];
    }),
  );
}

/** The member's pick in the popover, else the org whose pages they're on, else the first by name. */
export function pickOrgForRing(
  candidates: readonly MyOrganization[],
  routeSlug: string | null,
  pickedId: string | null,
): MyOrganization | null {
  return (
    candidates.find((o) => o.id === pickedId) ??
    candidates.find((o) => o.slug === routeSlug) ??
    candidates[0] ??
    null
  );
}

/** The device's calendar day, YYYY-MM-DD — the daily nudge's unit. */
export function localDay(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
